import { Import, X } from 'lucide-solid'
import { Component, For, Show, createSignal, onMount } from 'solid-js'
import FileInput, { FileInputResult } from '../../shared/FileInput'
import Modal from '../../shared/Modal'
import { characterStore, i18nStore, NewCharacter, toastStore } from '../../store'
import AvatarIcon from '/web/shared/AvatarIcon'
import { SUPPORTED_FORMATS, downloadCharacterHub, importCharacterFile } from './port'
import Button from '/web/shared/Button'

const MAX_SHOWN_IMPORTS = 3

const ImportCharacterModal: Component<{
  show: boolean
  close: () => void
  onSave: (chars: NewCharacter[], images: Array<File | undefined>) => void
  charhubPath?: string
  single?: boolean
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const state = characterStore((s) => ({ creating: s.creating }))
  const [imported, setImported] = createSignal<NewCharacter[]>([])
  const [images, setImages] = createSignal<Array<File | undefined>>([])
  const [failed, setFailed] = createSignal<string[]>([])
  const [ready, setReady] = createSignal(false)

  onMount(async () => {
    if (!props.charhubPath) return
    try {
      const { json } = await downloadCharacterHub(props.charhubPath)
      setImported([json])
      toastStore.success(i18n.t.char_characterHubDownloadSuccess)
      setReady(true)
    } catch (ex: any) {
      toastStore.error(i18n.t.char_characterHubDownloadFailed.replace('{message}', ex.message))
    }
  })

  const processFiles = async (files: FileInputResult[]) => {
    reset()

    const results = await Promise.allSettled(files.map(importCharacterFile))
    for (let i = 0; i < results.length; i++) {
      const result = results[i]
      if (result.status === 'rejected') {
        setFailed(failed().concat(files[i].file.name))
      } else {
        setImported(imported().concat(result.value.char))
        setImages(images().concat(result.value.image))
      }
    }

    setReady(imported().length > 0)
  }

  const onImport = async () => {
    if (!ready()) return
    props.onSave(imported(), images())
  }

  const reset = () => {
    setReady(false)
    setImages([])
    setImported([])
    setFailed([])
  }

  const cancel = () => {
    reset()
    props.close()
  }

  return (
    <Modal
      show={props.show}
      title={i18n.t.importCharacter}
      close={cancel}
      footer={
        <>
          <Button schema="secondary" onClick={cancel}>
            <X />
            {i18n.t.cancel}
          </Button>

          <Button onClick={onImport} disabled={state.creating || !ready()}>
            <Import />
            {i18n.t.importCharacter}
          </Button>
        </>
      }
    >
      <div class="flex flex-col gap-2">
        <FileInput
          label={i18n.t.char_avatarOrJsonFile}
          fieldName="file"
          accept="text/json,application/json,image/png,image/jpeg,image/webp"
          helperText={`${i18n.t.admin_replacementSubscriptionHelper} ${SUPPORTED_FORMATS}`}
          required
          multiple={!props.single}
          onUpdate={processFiles}
        />
      </div>

      <Show when={imported().length}>
        <div class="mt-2 text-lg">{i18n.t.char_charactersToImport}</div>
        <div class="markdown">
          <ul>
            <For each={imported().slice(0, MAX_SHOWN_IMPORTS)}>
              {(i) => (
                <li class="flex gap-2">
                  <AvatarIcon format={{ corners: 'circle', size: 'sm' }} avatarUrl={i.avatar} />
                  {i.name ?? i18n.t.char_unnamed}
                </li>
              )}
            </For>
            <Show when={imported().length === MAX_SHOWN_IMPORTS + 1}>
              <li>{i18n.t.char_andOneOther}</li>
            </Show>
            <Show when={imported().length > MAX_SHOWN_IMPORTS + 1}>
              <li>{i18n.t.char_andOthers.replace('{count}', (imported().length - MAX_SHOWN_IMPORTS).toString())}</li>
            </Show>
          </ul>
        </div>
      </Show>

      <Show when={failed().length}>
        <div class="mt-2 text-lg">{i18n.t.char_failedCharacterImports}</div>
        <div class="markdown">
          <ul>
            <For each={failed().slice(0, MAX_SHOWN_IMPORTS)}>
              {(i) => <li>{i ?? i18n.t.char_unnamed}</li>}
            </For>
            <Show when={failed().length === MAX_SHOWN_IMPORTS + 1}>
              <li>{i18n.t.char_andOneOther}</li>
            </Show>
            <Show when={failed().length > MAX_SHOWN_IMPORTS + 1}>
              <li>{i18n.t.char_andOthers.replace('{count}', (failed().length - MAX_SHOWN_IMPORTS).toString())}</li>
            </Show>
          </ul>
        </div>
      </Show>
    </Modal>
  )
}

export default ImportCharacterModal
